/**
 * ValidationMessages - Módulo para la gestión de mensajes de validación
 */
import $ from 'jquery';
import 'datatables.net-bs5';
import 'datatables.net-responsive-bs5';
import { getSpanishLanguage, } from '@/helpers';
export class ValidationMessages {
    constructor() {
        this.TABLE_ID = '#validationMessagesTable';
        this.dataTable = null;
        this.deactivateModal = null;
        this.activateModal = null;
        this.baseUrl = window.location.origin;
        this.init();
    }
    init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.initializeModule();
            });
        }
        else {
            this.initializeModule();
        }
    }
    initializeModule() {
        this.initializeTable();
        this.initializeModals();
        this.setupEventListeners();
    }
    setupEventListeners() {
        // Escuchar cambios de tema para ajustar la tabla si es necesario
        window.addEventListener('theme-changed', () => {
            this.adjustTableForTheme();
        });
        // Manejar clicks en botones de acción (delegación de eventos)
        document.addEventListener('click', (e) => {
            const target = e.target;
            const deactivateBtn = target.closest('[data-action="deactivate"]');
            const activateBtn = target.closest('[data-action="activate"]');
            if (deactivateBtn) {
                e.preventDefault();
                this.handleDeactivate(deactivateBtn);
            }
            if (activateBtn) {
                e.preventDefault();
                this.handleActivate(activateBtn);
            }
        });
    }
    adjustTableForTheme() {
        // Ajustes visuales cuando cambia el tema (si es necesario)
        if (this.dataTable) {
            this.dataTable.draw(false);
        }
    }
    initializeModals() {
        const deactivateModalEl = document.getElementById('deactivateModal');
        const activateModalEl = document.getElementById('activateModal');
        if (deactivateModalEl) {
            this.deactivateModal = new window.bootstrap.Modal(deactivateModalEl);
        }
        if (activateModalEl) {
            this.activateModal = new window.bootstrap.Modal(activateModalEl);
        }
    }
    handleDeactivate(button) {
        const id = button.getAttribute('data-id');
        if (!id) {
            console.error('Missing id attribute');
            return;
        }
        const confirmBtn = document.getElementById('confirmDeactivateBtn');
        if (confirmBtn) {
            confirmBtn.setAttribute('href', `${this.baseUrl}/settings/change-status/${id}`);
        }
        if (this.deactivateModal) {
            this.deactivateModal.show();
        }
    }
    handleActivate(button) {
        const id = button.getAttribute('data-id');
        if (!id) {
            console.error('Missing id attribute');
            return;
        }
        const confirmBtn = document.getElementById('confirmActivateBtn');
        if (confirmBtn) {
            confirmBtn.setAttribute('href', `${this.baseUrl}/settings/change-status/${id}`);
        }
        if (this.activateModal) {
            this.activateModal.show();
        }
    }
    initializeTable() {
        const tableElement = document.querySelector(this.TABLE_ID);
        if (!tableElement) {
            console.warn('ValidationMessages table element not found');
            return;
        }
        try {
            this.dataTable = $(this.TABLE_ID).DataTable(this.getTableConfig());
        }
        catch (error) {
            console.error('Error initializing DataTables:', error);
        }
    }
    getTableConfig() {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        return {
            processing: true,
            serverSide: true,
            responsive: false,
            language: getSpanishLanguage(),
            ajax: {
                url: `${this.baseUrl}/settings/get-list`,
                type: 'POST',
                data: {
                    'csrf_token': csrfToken || ''
                },
                error: (xhr, error, thrown) => {
                    console.error('Error al cargar los datos:', error);
                    const tbody = document.querySelector(`${this.TABLE_ID} tbody`);
                    if (tbody) {
                        tbody.innerHTML = '<tr><td colspan="6" class="text-center text-danger">Error al cargar los datos. Por favor, recargue la página.</td></tr>';
                    }
                }
            },
            columns: [
                {
                    data: 'message',
                    name: 'message',
                    className: 'text-nowrap',
                    render: (data) => this.renderMessage(data)
                },
                {
                    data: 'div_color',
                    name: 'div_color',
                    className: 'text-nowrap',
                    render: (data) => this.renderColor(data)
                },
                {
                    data: 'email_user',
                    name: 'email_user',
                    className: 'text-nowrap',
                },
                {
                    data: 'name_user',
                    name: 'name_user',
                    className: 'text-nowrap',
                },
                {
                    data: 'status_message',
                    name: 'status_message',
                    className: 'text-nowrap',
                    render: (data) => this.renderStatus(data)
                },
                {
                    data: 'date_created',
                    name: 'date_created',
                    className: 'text-nowrap',
                    render: (data) => this.renderDate(data)
                },
                {
                    data: 'id',
                    className: 'text-center text-nowrap col-acciones',
                    orderable: false,
                    render: (data, type, row) => this.renderAcciones(row)
                }
            ],
            pageLength: 25,
            lengthMenu: [[10, 25, 50, 100], [10, 25, 50, 100]]
        };
    }
    renderMessage(data) {
        const message = data || '';
        const maxLength = 80;
        if (message.length > maxLength) {
            return message.substring(0, maxLength) + '...';
        }
        return message;
    }
    renderColor(data) {
        const colors = {
            'primary': '<span class="badge bg-primary text-white">Azul</span>',
            'secondary': '<span class="badge bg-secondary text-white">Gris</span>',
            'success': '<span class="badge bg-success text-white">Verde</span>',
            'danger': '<span class="badge bg-danger text-white">Rojo</span>',
            'warning': '<span class="badge bg-warning text-dark">Amarillo</span>',
            'info': '<span class="badge bg-info text-white">Cian</span>',
            'light': '<span class="badge bg-light text-dark">Claro</span>',
            'dark': '<span class="badge bg-dark text-white">Oscuro</span>'
        };
        return colors[data] || '<span class="text-muted">-</span>';
    }
    renderStatus(data) {
        const status = parseInt(data);
        if (status === 1) {
            return '<span class="badge bg-success"><i class="bi bi-check-circle me-1"></i>Activo</span>';
        }
        else {
            return '<span class="badge bg-danger"><i class="bi bi-x-circle me-1"></i>Inactivo</span>';
        }
    }
    renderDate(data) {
        if (!data)
            return '<span class="text-muted">-</span>';
        try {
            const date = new Date(data);
            return date.toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
        catch (e) {
            return data;
        }
    }
    renderAcciones(row) {
        const { id, status_message, can_edit, can_change_status, } = row;
        const status = parseInt(status_message);
        let buttons = `
            <div class="btn-group" role="group">
                ${can_edit ? `
                        <a href="${this.baseUrl}/settings/edit/${id}"
                        class="btn btn-sm btn-outline-primary"
                        title="Editar">
                            <i class="bi bi-pencil"></i>
                        </a>` : ''}
        `;
        if (can_change_status) {
            if (status === 1) {
                buttons += `
                    <button type="button"
                            class="btn btn-sm btn-outline-danger"
                            data-action="deactivate"
                            data-id="${id}"
                            title="Desactivar">
                        <i class="bi bi-dash-circle"></i>
                    </button>
                `;
            }
            else {
                buttons += `
                    <button type="button"
                            class="btn btn-sm btn-outline-success"
                            data-action="activate"
                            data-id="${id}"
                            title="Activar">
                        <i class="bi bi-check-circle"></i>
                    </button>
                `;
            }
        }
        buttons += '</div>';
        return buttons;
    }
    /**
     * Obtiene la instancia del DataTable
     */
    getTable() {
        return this.dataTable;
    }
    /**
     * Recarga los datos de la tabla
     * @param resetPaging Si es true, resetea la paginación a la primera página
     */
    reload(resetPaging = false) {
        if (this.dataTable) {
            this.dataTable.ajax.reload(null, resetPaging);
        }
    }
    /**
     * Obtiene los datos seleccionados en la tabla
     */
    getSelectedRows() {
        if (this.dataTable) {
            return this.dataTable.rows('.selected').data().toArray();
        }
        return [];
    }
    /**
     * Filtra la tabla por una columna específica
     * @param column Índice de la columna
     * @param value Valor a buscar
     */
    filterByColumn(column, value) {
        if (this.dataTable) {
            this.dataTable.column(column).search(value).draw();
        }
    }
    /**
     * Limpia todos los filtros de la tabla
     */
    clearFilters() {
        if (this.dataTable) {
            this.dataTable.search('').columns().search('').draw();
        }
    }
}
export default ValidationMessages;
